import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Download, ExternalLink } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import {
  downloadFeedbackAttachment,
  getApiErrorMessage,
  viewFeedbackAttachment,
} from '../../utils/feedbackDownload';
import { openPdfBlob, downloadBlob } from '../../utils/blobDownload';
import { isNativePlatform } from '../../utils/device';

function isImageAttachment(att) {
  if (att?.is_image) return true;
  const mime = String(att?.mime_type || '').toLowerCase();
  if (mime.startsWith('image/')) return true;
  const name = String(att?.original_name || '').toLowerCase();
  return /\.(png|jpe?g)$/.test(name);
}

function isPdfAttachment(att) {
  const mime = String(att?.mime_type || '').toLowerCase();
  if (mime === 'application/pdf') return true;
  return String(att?.original_name || '').toLowerCase().endsWith('.pdf');
}

function attachmentTypeLabel(att) {
  if (isPdfAttachment(att)) return 'PDF';
  if (att?.file_type === 'screenshot') return 'Screenshot';
  return 'Attachment';
}

function AttachmentPreview({ attachment, onDownload, onOpen }) {
  const [previewUrl, setPreviewUrl] = useState('');
  const [loading, setLoading] = useState(isImageAttachment(attachment));
  const [error, setError] = useState('');
  const isImage = isImageAttachment(attachment);

  useEffect(() => {
    if (!isImage) return undefined;

    let active = true;
    let objectUrl = '';

    (async () => {
      try {
        const blob = await viewFeedbackAttachment(attachment.id);
        if (!active) return;
        objectUrl = URL.createObjectURL(blob);
        setPreviewUrl(objectUrl);
      } catch (e) {
        if (active) {
          setError(await getApiErrorMessage(e));
        }
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [attachment.id, isImage]);

  if (isImage) {
    return (
      <div className="rounded-lg border border-border p-2">
        {loading && <p className="text-sm text-muted-foreground">Loading preview…</p>}
        {!loading && error && <p className="text-sm text-destructive">{error}</p>}
        {!loading && previewUrl && (
          <button
            type="button"
            onClick={() => onOpen(attachment)}
            className="block w-full"
          >
            <img
              src={previewUrl}
              alt={attachmentTypeLabel(attachment)}
              className="max-h-80 w-full object-contain rounded"
            />
          </button>
        )}
      </div>
    );
  }

  const label = attachmentTypeLabel(attachment);

  return (
    <div className="rounded-lg border border-border p-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium text-muted-foreground">{label}</p>
        <div className="flex items-center gap-2 shrink-0">
          {isPdfAttachment(attachment) && (
            <Button type="button" variant="outline" size="sm" onClick={() => onOpen(attachment)}>
              <ExternalLink className="h-4 w-4 mr-1" />
              Open
            </Button>
          )}
          <Button type="button" variant="outline" size="sm" onClick={() => onDownload(attachment)}>
            <Download className="h-4 w-4 mr-1" />
            Download
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function FeedbackAttachments({ attachments = [], canAccess = true }) {
  const { toast } = useToast();
  const items = attachments || [];
  const screenshots = items.filter((a) => a.file_type === 'screenshot');
  const files = items.filter((a) => a.file_type !== 'screenshot');

  const handleDownload = async (att) => {
    try {
      await downloadFeedbackAttachment(att.id, att.original_name);
      toast({ title: 'Download started', description: att.original_name || 'Attachment saved.' });
    } catch (e) {
      toast({ title: 'Download failed', description: await getApiErrorMessage(e), variant: 'destructive' });
    }
  };

  const handleOpen = async (att) => {
    try {
      const blob = await viewFeedbackAttachment(att.id);
      if (isPdfAttachment(att)) {
        const result = await openPdfBlob(blob, att.original_name || 'attachment.pdf');
        if (result?.native) {
          toast({ title: 'Saved to Downloads', description: att.original_name || 'PDF saved.' });
        }
        return;
      }
      if (isNativePlatform()) {
        await downloadBlob(blob, att.original_name || 'attachment');
        toast({ title: 'Saved to Downloads', description: att.original_name || 'File saved.' });
        return;
      }
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 200);
    } catch (e) {
      toast({ title: 'Could not open file', description: await getApiErrorMessage(e), variant: 'destructive' });
    }
  };

  if (!canAccess) {
    return null;
  }

  return (
    <Card>
      <CardHeader><CardTitle>Attachments</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground">No files were attached to this feedback.</p>
        ) : (
          <>
            {screenshots.length > 0 && (
              <div className="space-y-3">
                <p className="text-sm font-medium text-muted-foreground">Screenshots</p>
                {screenshots.map((att) => (
                  <AttachmentPreview
                    key={att.id}
                    attachment={att}
                    onDownload={handleDownload}
                    onOpen={handleOpen}
                  />
                ))}
              </div>
            )}
            {files.length > 0 && (
              <div className="space-y-3">
                <p className="text-sm font-medium text-muted-foreground">Files</p>
                {files.map((att) => (
                  <AttachmentPreview
                    key={att.id}
                    attachment={att}
                    onDownload={handleDownload}
                    onOpen={handleOpen}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
